/**
 * usePremiumGuard — Gestion centralisée des popups premium
 *
 * Comportement :
 *   - `guardCheck(type, count, auto)` → boolean (allowed)
 *     - En auto=true : popup 1 fois par visite d'écran (pour avertissement passif)
 *     - En auto=false (appel depuis action utilisateur) : popup SYSTÉMATIQUE à chaque blocage
 *   - Remplit `limitInfo` avec tier courant + usage → utilisé par UpgradeModal
 *   - Écoute AUSSI l'event `ai_limit_reached` émis par AIStylistService.guardAIQuota()
 *     pour que la popup s'affiche automatiquement sur tous les écrans dès qu'un
 *     appel IA est bloqué (même si guardCheck n'a pas été appelé explicitement).
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { DeviceEventEmitter, InteractionManager } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { checkLimit } from '../services/PremiumService';
import { useAuth } from '../context/AuthContext';
import { showAppAlert } from '../components/AppDialog';

// ═══ Singleton global : dernier event traité ═══════════════
// Plusieurs écrans peuvent être "focused" simultanément (Stack push +
// modal au-dessus). Sans ce verrou, chaque instance du hook ouvrirait
// son popup → user voit 2-3 popups empilés et doit cliquer "Plus tard"
// plusieurs fois. Avec : seule la 1ère instance qui reçoit l'event dans
// la fenêtre de 500ms ouvre le popup. Les autres ignorent.
const _lastEventStamp = { ai: 0, wardrobe: 0, tryon: 0 };
const COALESCE_WINDOW_MS = 500;

function shouldHandleEvent(type) {
  // v1.12.806 — Retrait du sticky-block sur les events :
  // Les events `tryon_limit_reached` / `ai_limit_reached` / `wardrobe_limit_reached`
  // sont émis par des ACTIONS UTILISATEUR (click Try-on, click générer IA,
  // ajout article). Bloquer ces popups laisse l'user sans CTA d'upgrade.
  // Le sticky block post-Stripe reste appliqué SEULEMENT aux guardCheck(auto=true)
  // (passive focus checks) où il a du sens — voir guardCheck plus bas.
  const now = Date.now();
  if (now - _lastEventStamp[type] < COALESCE_WINDOW_MS) return false;
  _lastEventStamp[type] = now;
  return true;
}

export function usePremiumGuard(user) {
  const [showPremium,    setShowPremium]    = useState(false);
  const [premiumTrigger, setPremiumTrigger] = useState('wardrobe');
  const [limitInfo,      setLimitInfo]      = useState(null);
  const shownThisVisit = useRef(false);
  // v1.12.879 — navigation pour rediriger vers Login si user pas connecté
  let navigation;
  try { navigation = useNavigation(); } catch {}

  // Reset à chaque visite de l'écran
  useFocusEffect(useCallback(() => {
    shownThisVisit.current = false;
  }, []));

  // ═══ Listeners actifs UNIQUEMENT sur l'écran focus ═══════
  useFocusEffect(useCallback(() => {
    const aiSub = DeviceEventEmitter.addListener('ai_limit_reached', (payload) => {
      // Coalesce : si un autre hook a déjà traité cet event dans les 500ms,
      // on ignore. Évite les popups empilés.
      if (!shouldHandleEvent('ai')) {
        console.log('[PremiumGuard] 🔇 AI event coalesced (déjà traité <500ms)');
        return;
      }
      console.log('[PremiumGuard] 🚨 AI limit event received:', payload);
      setLimitInfo({
        tier:    payload?.tier || 'free',
        limit:   payload?.limit || 0,
        current: payload?.current || 0,
        reason:  ((() => { let fr = true; try { fr = require('../i18n').getCurrentLang().startsWith('fr'); } catch {} return fr; })())
          ? `Limite IA atteinte (${payload?.current || 0}/${payload?.limit || 0} aujourd'hui)`
          : `AI limit reached (${payload?.current || 0}/${payload?.limit || 0} today)`,
        trigger: 'ai',
      });
      setPremiumTrigger('ai');
      // v1.13.58 — FIX flash Android : on DIFFÈRE l'ouverture de la popup pour
      // laisser un éventuel autre Modal (AddItemModal / progression d'ajout) se
      // fermer d'abord. Sur Android, 2 <Modal> RN ne cohabitent pas : ouvrir la
      // popup pendant qu'un autre modal se ferme la dismisse aussitôt (elle
      // "apparaît puis disparaît"). 400ms couvre l'animation de fermeture.
      // v1.13.67 — plus robuste que le délai fixe : on attend la fin des
      // interactions/animations en cours (fermeture du modal source try-on /
      // flat-lay / add) PUIS 350ms → la popup s'ouvre seule, sans conflit 2-Modals
      // Android (qui la faisait "apparaître puis disparaître").
      InteractionManager.runAfterInteractions(() => setTimeout(() => setShowPremium(true), 350));
    });

    const wardrobeSub = DeviceEventEmitter.addListener('wardrobe_limit_reached', (payload) => {
      if (!shouldHandleEvent('wardrobe')) {
        console.log('[PremiumGuard] 🔇 Wardrobe event coalesced (déjà traité <500ms)');
        return;
      }
      console.log('[PremiumGuard] 🚨 Wardrobe limit event received:', payload);
      setLimitInfo({
        tier:    payload?.tier || 'free',
        limit:   payload?.limit || 0,
        current: payload?.current || 0,
        reason:  `Dressing plein (${payload?.current || 0}/${payload?.limit || 0} pièces). Passez à un plan supérieur pour en ajouter plus.`,
        trigger: 'wardrobe',
      });
      setPremiumTrigger('wardrobe');
      // v1.13.64 — FIX flash Android : différer (cf. ai/tryon).
      // v1.13.67 — plus robuste que le délai fixe : on attend la fin des
      // interactions/animations en cours (fermeture du modal source try-on /
      // flat-lay / add) PUIS 350ms → la popup s'ouvre seule, sans conflit 2-Modals
      // Android (qui la faisait "apparaître puis disparaître").
      InteractionManager.runAfterInteractions(() => setTimeout(() => setShowPremium(true), 350));
    });

    // v1.12.650 — Force close paywall au retour de Stripe (sinon UpgradeModal
    // reste visible et l'user click sur un tier → re-checkout en boucle)
    const forceCloseSub = DeviceEventEmitter.addListener('__stripe_force_close_paywalls__', () => {
      console.log('[PremiumGuard] 🧹 force close (post-Stripe)');
      setShowPremium(false);
      setLimitInfo(null);
    });

    // v1.12.122 : try-on gate (free n'a plus acces, basic/premium gere quota)
    // v1.12.879 : si user non connecté → invite login au lieu d'afficher quota mensuel
    const tryonSub = DeviceEventEmitter.addListener('tryon_limit_reached', (payload) => {
      if (!shouldHandleEvent('tryon')) return;
      console.log('[PremiumGuard] tryon limit event:', payload);
      // Auth gate : si pas de user → popup login (pas de modal upgrade trompeuse)
      if (!user) {
        try {
          showAppAlert(
            'Connexion requise',
            'Connectez-vous pour utiliser l\'essai virtuel et le Flat-Lay.',
            [
              { text: 'Annuler', style: 'cancel' },
              { text: 'Se connecter', onPress: () => {
                try { navigation?.navigate?.('Login'); } catch (e) { console.warn('[PremiumGuard] nav Login:', e?.message); }
              }},
            ]
          );
        } catch {}
        return;
      }
      setLimitInfo({
        tier:    payload?.tier || user?.tier || 'free',
        limit:   payload?.limit || 0,
        current: payload?.current || 0,
        // v1.12.493 — propage nextResetDate pour affichage exact dans UpgradeModal
        // v1.12.879 — Ne propager nextResetDate QUE pour tiers payants (free n'a pas de cycle)
        nextResetDate: (payload?.tier === 'free' || user?.tier === 'free') ? null : (payload?.nextResetDate || null),
        isPaidTier: payload?.isPaidTier || false,
        reason:  payload?.tier === 'free'
          ? 'Essai virtuel reserve aux abonnes payants'
          : `Essais epuises (${payload?.current || 0}/${payload?.limit || 0})`,
        trigger: 'tryon',
      });
      setPremiumTrigger('tryon');
      // v1.13.64 — FIX flash Android (comme l'IA) : différer l'ouverture pour
      // laisser le modal try-on/flat-lay se fermer d'abord (2 <Modal> RN ne
      // cohabitent pas → la popup "apparaît puis disparaît").
      // v1.13.67 — plus robuste que le délai fixe : on attend la fin des
      // interactions/animations en cours (fermeture du modal source try-on /
      // flat-lay / add) PUIS 350ms → la popup s'ouvre seule, sans conflit 2-Modals
      // Android (qui la faisait "apparaître puis disparaître").
      InteractionManager.runAfterInteractions(() => setTimeout(() => setShowPremium(true), 350));
    });

    return () => {
      aiSub.remove();
      wardrobeSub.remove();
      tryonSub.remove();
    };
  }, []));

  /**
   * Vérifier une limite
   * @param {string} type        - 'wardrobe' | 'ai' | 'looks' | 'trips'
   * @param {number} count       - Valeur actuelle (nb pièces, nb requêtes...)
   * @param {boolean} auto       - true = vérification auto (1 seul popup/visite), false = action utilisateur (popup à chaque fois)
   * @returns {boolean}          - true = action autorisée
   */
  const guardCheck = useCallback(async (type, count = 0, auto = false) => {
    const limit = await checkLimit(type, count, user);
    console.log(`[PremiumGuard] check type=${type} count=${count} auto=${auto} → allowed=${limit.allowed} tier=${limit.tier} limit=${limit.limit} reason=${limit.reason}`);
    if (limit.allowed) return true;

    // En mode auto, ne montrer qu'une fois par visite
    if (auto && shownThisVisit.current) return false;
    if (auto) shownThisVisit.current = true;

    // v1.12.654 — STICKY block guardCheck auto après retour Stripe
    if (auto) {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          if (window.sessionStorage.getItem('__stripe_session_block_auto__') === '1') {
            console.log('[PremiumGuard] 🔇 guardCheck auto blocked (post-Stripe sticky)');
            return false;
          }
        }
      } catch {}
    }

    console.warn(`[PremiumGuard] 🚨 BLOQUÉ type=${type} → popup affiché (current=${limit.current}/${limit.limit}, tier=${limit.tier})`);

    // Enregistrer les détails de la limite pour que le Modal UpgradeModal puisse s'afficher
    setLimitInfo({
      tier:    limit.tier || 'free',
      limit:   limit.limit,
      current: limit.current,
      reason:  limit.reason,
      trigger: limit.trigger || type,
    });
    setPremiumTrigger(type);
    // v1.13.90 — FIX "popup quota IA s'affiche et disparaît auto" : setShowPremium(true)
    //   était DIRECT → si guardCheck est appelé pendant une transition d'écran
    //   (focus effect à l'arrivée sur le Dressing) ou pendant qu'un autre Modal se
    //   ferme (clic "+"), la popup s'ouvrait puis était dismissée aussitôt (conflit
    //   2-Modals Android). On DIFFÈRE l'ouverture après la fin des interactions +
    //   250ms, comme les listeners d'events (v1.13.67).
    InteractionManager.runAfterInteractions(() => setTimeout(() => setShowPremium(true), 250));
    return false;
  }, [user]);

  const closePremium = useCallback(() => {
    setShowPremium(false);
    setLimitInfo(null);
  }, []);

  return {
    showPremium,
    premiumTrigger,
    limitInfo,
    guardCheck,
    closePremium,
    setShowPremium,
    setPremiumTrigger,
    setLimitInfo, // v1.12.580 — exposé pour tap item hidden (V5.1-bis)
  };
}
